import React from 'react'
import { View } from '@components/atoms'

import config from '@configs'
import {
	PayPalScriptProvider,
	ReactPayPalScriptOptions,
	PayPalButtons,
} from '@paypal/react-paypal-js'
import { OnApproveData } from '@paypal/paypal-js'
import { ResType, paymentApi } from '@api'
import {
	useAppSelector,
	useAppDispatch,
	paymentActions,
	routeActions,
	PaymentStateType,
	PaymentDeliveryType,
	CartType,
	PaymentCustomerInfoType,
	PaymentCostType,
	PaymentShippingType,
	CheckoutQuoteType,
	CheckoutQuoteProblemType,
} from '@redux/index'
import { Validator, ValidatorResultType } from '@validators'
import { Analytics, Logger, CartQuote, CartKey, checkoutAuthHeaders } from '@utils'
import { produce } from 'immer'

/**
 * 결제 화면의 PayPal 블록 — Monolith(2안).
 *
 * 2026-09-16 (judahsWeb/CHECKOUT_PLAN.md §4.1):
 *   - 주문 생성 요청에 금액을 보내지 않는다. 서버가 견적으로 PayPal 주문을 만들고 임시주문(orderId)과
 *     `cancelToken` 을 돌려준다. 승인 창을 닫으면 그 표로 임시주문을 되돌린다.
 *   - 승인 응답에 주문번호·청구액이 실려 온다 → 성공 화면으로 간다(전에는 모달 뒤 홈으로 갔다).
 * 버튼 자체는 PayPal 이 iframe 으로 그린다 — 색은 모노크롬에 맞는 `black`.
 */
export const MonolithPaypalPayment = () => {
	// redux dispatch
	const reduxDispatch = useAppDispatch()

	// redux state
	const cartList: CartType[] = useAppSelector(state => state.payment.tryToPayment)[
		'cartList'
	]
	const customerId: string = useAppSelector(state => state.customerAuth.customer).id
	const customerInfo: PaymentCustomerInfoType = useAppSelector(
		state => state.payment.paymentCustomerInfo,
	)
	const delivery: PaymentDeliveryType = useAppSelector(
		state => state.payment.paymentDelivery,
	)
	const shipping: PaymentShippingType = useAppSelector(
		state => state.payment.paymentShipping,
	)
	const cost: PaymentCostType = useAppSelector(state => state.payment.paymentCost)
	const quote: CheckoutQuoteType | null = useAppSelector(state => state.payment.quote)
	const errorMessages: PaymentStateType['paymentErrorMessages'] = useAppSelector(
		state => state.payment.paymentErrorMessages,
	)

	// state
	const [paypalErrorMessage, setPaypalErrorMessage] = React.useState('')
	/** 서버가 만든 임시주문. 승인 창을 닫으면 되돌린다 */
	const pendingRef = React.useRef<{ orderId: string; cancelToken: string } | null>(null)

	const payPalConfig: ReactPayPalScriptOptions = React.useMemo(() => {
		const res = {
			...config.get('paypal'),
			disableFunding: ['card'],
		}
		return res
	}, [delivery])

	const body = () => ({
		items: CartQuote.toQuoteItems(cartList, 'USD'),
		customer: {
			givenName: customerInfo.givenName,
			familyName: customerInfo.familyName,
			email: customerInfo.email,
			phoneCountryCode: customerInfo.phoneCountryCode,
			phoneNumber: customerInfo.phoneNumber,
		},
		delivery: delivery,
		shipping: shipping,
		cost: { couponCode: cost.couponCode },
		customerId: customerId,
		cartKey: CartKey.get(),
	})

	const onQuoteMismatch = (apiRes: ResType | undefined) => {
		const detail: { problems?: CheckoutQuoteProblemType[] } | undefined =
			apiRes?.exception?.message?.meta?.detail
		const problems = detail?.problems ?? []
		reduxDispatch(paymentActions.QUOTE_PROBLEMS(problems))
		reduxDispatch(paymentActions.TRIGGER_QUOTE(true))
		reduxDispatch(
			paymentActions.MODAL_INFO({ target: 'quoteChanged', title: 'The cart changed' }),
		)
		reduxDispatch(paymentActions.SHOW_MODAL(true))
	}

	const onPaypalCreatingOrder = async () => {
		try {
			const res: ValidatorResultType = Validator.payment({
				// customer
				phoneCountryCode: customerInfo.phoneCountryCode,
				phoneNumber: customerInfo.phoneNumber,

				// delivery
				countryName: delivery.countryName,
				states: delivery.stateName,
				city: delivery.city,
				street1: delivery.street1,
				zip: delivery.zip,

				// shipping
				deliveryCompanyName: shipping.companyName,
			})

			const filterInvalidRes = Object.keys(res).filter(aKey => !res[aKey].isValid)
			const isSuccess = filterInvalidRes.length === 0

			const newErrorMessage = produce(errorMessages, draft => {
				draft.customer.phoneCountryCode = res['phoneCountryCode'].message
				draft.customer.phoneNumber = res['phoneNumber'].message

				draft.delivery.countryName = res['countryName'].message
				draft.delivery.states = res['states'].message
				draft.delivery.city = res['city'].message
				draft.delivery.street1 = res['street1'].message
				draft.delivery.zip = res['zip'].message

				draft.card.cardNumber = ''
				draft.card.expiredMonth = ''
				draft.card.expiredYear = ''
				draft.card.cvc = ''
				draft.card.cardName = ''

				draft.shipping.companyName = res['deliveryCompanyName'].message

				draft.isSuccess = isSuccess
			})

			reduxDispatch(paymentActions.PAYMENT_ERROR_MESSAGES({ ...newErrorMessage }))

			if (!newErrorMessage.isSuccess) {
				setPaypalErrorMessage(
					'Some required fields may be missing, or certain inputs may be in an incorrect format.',
				)
				throw new Error('')
			}

			const apiRes: ResType | undefined = await paymentApi.createPaypalOrder({
				body: body(),
				customHeaders: checkoutAuthHeaders(),
			})

			if (apiRes !== undefined && apiRes.status === 201) {
				const data = apiRes.data[0]
				pendingRef.current = {
					orderId: data.orderId ?? '',
					cancelToken: data.cancelToken ?? '',
				}
				return data.payPalOrderId as string
			}

			if (apiRes?.exception?.message?.meta?.title === '[CHECKOUT]:QUOTE-MISMATCH') {
				onQuoteMismatch(apiRes)
				throw new Error('')
			}

			setPaypalErrorMessage('Could not initiate PayPal Checkout.')
			throw new Error('')
		} catch (error) {
			Logger.warn(error)
			return ''
		}
	}

	const onPaypalApproval = async (data: OnApproveData) => {
		try {
			const payPalOrderId = data.orderID
			const apiRes: ResType | undefined = await paymentApi.paypalOrderApproval({
				body: {
					...body(),
					payPalOrderId: payPalOrderId,
				},
				customHeaders: checkoutAuthHeaders(),
			})

			if (apiRes !== undefined && apiRes.status === 201) {
				const summary = apiRes.data[0]
				pendingRef.current = null

				Analytics.purchase({
					transactionId: summary.orderId ?? payPalOrderId,
					cartList: cartList,
					value: Number(summary.paidAmount ?? quote?.paidAmount ?? 0),
					currency: 'USD',
					shipping: Number(summary.shippingAmount ?? 0),
					coupon: cost.couponCode,
					paymentType: 'paypal',
				})

				onPaymentSuccess(summary)
			} else {
				setPaypalErrorMessage(
					'Sorry, your transaction could not be processed. Please contact us if you were charged.',
				)
				throw new Error('')
			}
		} catch (error) {
			console.error(error)
		}
	}

	/** 승인 창을 닫았다 — 방금 만든 임시주문을 되돌린다 */
	const onPaypalCancel = async () => {
		const pending = pendingRef.current
		pendingRef.current = null

		if (pending !== null && pending.orderId !== '') {
			await paymentApi.cancelCardOrder({
				body: { orderId: pending.orderId, cancelToken: pending.cancelToken },
				customHeaders: checkoutAuthHeaders(),
			})
		}
	}

	const onPaymentSuccess = (summary: { [key: string]: any }) => {
		const orderDetail = cartList
			.map((anItem: CartType) => `${anItem.productName} x ${anItem.qty}`)
			.join(',\n')
		const orderAddress = [
			delivery.street2,
			delivery.street1,
			delivery.city,
			delivery.countryIsoCode === 'US' ? delivery.stateIsoCode : delivery.stateName,
			delivery.countryName,
		].join(' ')

		reduxDispatch(
			paymentActions.PAYMENT_SUCCESS({
				orderId: summary.orderId ?? '',
				orderDetail: orderDetail,
				address: orderAddress,
				email: summary.customerEmail ?? customerInfo.email,
				phoneNumber: `+${customerInfo.phoneCountryCode} ${customerInfo.phoneNumber}`,
				currency: summary.currency ?? 'USD',
				paidAmount: Number(summary.paidAmount ?? 0),
				confirming: false,
				cancelToken: '',
			}),
		)
		setPaypalErrorMessage('')

		// remove cart
		localStorage.setItem('cart', '')
		window.dispatchEvent(new Event('storage'))

		// remove payment data
		reduxDispatch(paymentActions.TRIGGER_RESET_PAYMENT_DATA(true))
		reduxDispatch(routeActions.NAVIGATE_PATH('/payment-success'))
	}

	return (
		<View className="mnPayAction">
			<View className="mnPaypal">
				<PayPalScriptProvider options={payPalConfig} data-sdk-integration-source="button">
					<PayPalButtons
						style={{
							shape: 'rect',
							layout: 'vertical',
							color: 'black',
							label: 'buynow',
						}}
						disabled={quote === null || !quote.ok}
						createOrder={async () => {
							const res = (await onPaypalCreatingOrder()) ?? ''
							return res
						}}
						onApprove={onPaypalApproval}
						onCancel={() => {
							void onPaypalCancel()
						}}
						onError={err => {
							console.error('PayPal 결제 오류:', err)
							void onPaypalCancel()
						}}
						fundingSource={undefined}
					/>
				</PayPalScriptProvider>
			</View>

			{paypalErrorMessage !== '' && <span className="mnError">{paypalErrorMessage}</span>}
		</View>
	)
}
